// @ts-strict-ignore
import clsx from 'clsx'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Fragment, useEffect } from 'react'

import { BannerSlider } from '@/screens/promo-apipromo/ui/BannerSlider'
import { BannerSliderSert } from '@/screens/promo-apipromo/ui/BannerSliderSert'
import { BenefitsSection } from '@/screens/promo-apipromo/ui/benefits-section/BenefitsSection'
import { CustomImageBanner } from '@/screens/promo-apipromo/ui/CustomImageBanner/CustomImageBanner'
import { listData, stepsItems } from '@/screens/promo-apipromo/ui/data'
import { ExtraList } from '@/screens/promo-apipromo/ui/ExtraList'
import { GiftsSection } from '@/screens/promo-apipromo/ui/gifts-section/GiftsSection'
import { ListSection } from '@/screens/promo-apipromo/ui/ListSection'
import { Sertificate } from '@/screens/promo-apipromo/ui/sertificate/Sertificate'
import { WhyPort } from '@/screens/promo-apipromo/ui/why-port/WhyPort'

import { ObserverList } from '@/screensComponents/promo/ObserverList/ObserverList'

import { ProductCard } from '@/widgets/ProductCard'
import { VerticalSteps } from '@/widgets/VerticalSteps'

import { BASE_URL } from '@/shared/lib/const/env'
import { useMedia } from '@/shared/lib/hooks/useMedia'
import { createMarkup } from '@/shared/lib/utils/common/createMarkup'
import { Button } from '@/shared/ui'

import { FullWidthWrap } from '../../../shared/ui/FullWidthWrap'

import { GridWithBannerSection } from './GridWithBannerSection/GridWithBannerSection'

import cl from './index.module.scss'

type TPromoPage = {
	blockData: any
	productCounter?: number
}

const SCROLL_OFFSET = 220

const scrollToElement = (elementId: string, offset: number = SCROLL_OFFSET): void => {
	const element = document.getElementById(elementId)
	if (!element) return

	const elementPosition = element.getBoundingClientRect().top + window.scrollY
	const offsetPosition = elementPosition - offset

	window.scrollTo({
		top: offsetPosition,
		behavior: 'smooth'
	})
}

export const PromoApiPromo = ({ blockData }: TPromoPage): JSX.Element => {
	const { isLessThan } = useMedia()
	const searchParams = useSearchParams()
	const blocks = Array.isArray(blockData) ? blockData : []

	useEffect(() => {
		const scrollToId = searchParams?.get('scrollto')
		if (scrollToId) {
			setTimeout(() => scrollToElement(scrollToId), 100)
		}
	}, [searchParams])

	// Функция для разделения товаров в зависимости от размера экрана
	const sliceBr = (arr: unknown[]) => {
		if (isLessThan('992')) {
			return arr.length
		}
		if (isLessThan('1192')) {
			return 1
		}
		if (isLessThan('1600')) {
			return 2
		}
		return 3
	}

	return (
		<div className={cl.main}>
			{blocks.map((block, index) => {
				switch (block.type) {
					case 'top_banner': {
						const bannerComponent = (
							<CustomImageBanner
								key={index}
								imgDeskHeight={450}
								imgDeskWidth={1440}
								imgTabletHeight={768}
								imgTabletWidth={768}
								imgMobHeight={768}
								imgMobWidth={768}
								imgSrcDesk={BASE_URL + block.banners.banner_desktop.src}
								imgSrcTablet={BASE_URL + block.banners.banner_tablet.src}
								imgSrcMob={BASE_URL + block.banners.banner_mobile.src}
								link="/"
								className={cl.wideBanner}
								disabled
							/>
						)

						return block.fullWidth ? (
							<FullWidthWrap key={index} maxWidth={1440}>
								{bannerComponent}
							</FullWidthWrap>
						) : (
							bannerComponent
						)
					}

					case 'simple_banner': {
						const bannerComponent = (
							<Fragment key={index}>
								<h2 className={cl.sectionTitle}>{block.title}</h2>
								<CustomImageBanner
									key={index}
									imgDeskHeight={400}
									imgDeskWidth={1440}
									imgTabletHeight={768}
									imgTabletWidth={768}
									imgMobHeight={768}
									imgMobWidth={768}
									imgSrcDesk={BASE_URL + block.banners.banner_desktop.src}
									imgSrcTablet={BASE_URL + block.banners.banner_tablet.src}
									imgSrcMob={BASE_URL + block.banners.banner_mobile.src}
									link={block.banners.banner_desktop.link}
									className={cl.wideBanner}
									disabled={!block.banners.banner_desktop.link}
								/>
							</Fragment>
						)

						return block.fullWidth ? (
							<FullWidthWrap key={index} maxWidth={1440}>
								{bannerComponent}
							</FullWidthWrap>
						) : (
							bannerComponent
						)
					}

					case 'products': {
						const products = block.products ?? []

						return (
							<section key={index} id={block.code} className={cl.section}>
								<h2 className={cl.sectionTitle}>{block.title}</h2>
								<div className={cl.row} style={{ justifyContent: products.length < 5 ? 'center' : 'unset' }}>
									{products.slice(0, 5).map((item, index) => (
										<ProductCard key={index} data={item} noFastOrder={true} adaptive={true} className={cl.card} />
									))}
								</div>
								{!!block.show_all_link && (
									<Link prefetch={false} href={block.show_all_link}>
										<Button theme={'outlined'} className={cl.btn}>
											Смотреть все
										</Button>
									</Link>
								)}
							</section>
						)
					}

					case 'products_plus_banner': {
						const products = block.products ?? []
						const isEven = block.banner_position === `banner_left`

						if (!isEven) {
							return (
								<section key={index} id={block.code} className={cl.section}>
									{/* Баннер для четного блока */}
									<CustomImageBanner
										imgDeskWidth={556}
										imgDeskHeight={468}
										imgTabletWidth={620}
										imgTabletHeight={500}
										imgMobWidth={600}
										imgMobHeight={560}
										imgSrcDesk={BASE_URL + block.banners.banner_desktop.src}
										imgSrcTablet={BASE_URL + block.banners.banner_tablet.src}
										imgSrcMob={BASE_URL + block.banners.banner_mobile.src}
										link={block.banners.banner_desktop.link}
										className={cl.grid__item_banner_mob}
										lazy
									/>
									<div className={cl.grid}>
										<GridWithBannerSection
											items={products
												.filter(item => !!item.SECTION_NAME)
												.slice(0, 8)
												.slice(0, sliceBr(products))}
										/>

										{!!block.banners.banner_desktop.src && (
											<CustomImageBanner
												imgDeskWidth={556}
												imgDeskHeight={468}
												imgTabletWidth={620}
												imgTabletHeight={500}
												imgMobWidth={600}
												imgMobHeight={560}
												imgSrcDesk={BASE_URL + block.banners.banner_desktop.src}
												imgSrcTablet={BASE_URL + block.banners.banner_tablet.src}
												imgSrcMob={BASE_URL + block.banners.banner_mobile.src}
												link={block.banners.banner_desktop.link}
												className={cl.grid__item_banner}
												lazy
											/>
										)}

										{/* Вторая часть товаров */}
										<GridWithBannerSection
											items={products
												.filter(item => !!item.SECTION_NAME)
												.slice(0, 8)
												.slice(sliceBr(products))}
										/>
									</div>
									<Link prefetch={false} href={block.banners.banner_desktop.link}>
										<Button theme={'outlined'} className={cl.btn}>
											Смотреть все
										</Button>
									</Link>
								</section>
							)
						} else {
							// Логика для нечетного блока товаров
							return (
								<section key={index} id={block.code} className={clsx(cl.section, cl.mt)}>
									{/* Баннер для нечетного блока */}
									<CustomImageBanner
										imgDeskWidth={556}
										imgDeskHeight={468}
										imgTabletWidth={620}
										imgTabletHeight={500}
										imgMobWidth={600}
										imgMobHeight={560}
										imgSrcDesk={BASE_URL + block.banners.banner_desktop.src}
										imgSrcTablet={BASE_URL + block.banners.banner_tablet.src}
										imgSrcMob={BASE_URL + block.banners.banner_mobile.src}
										link={block.banners.banner_desktop.link}
										className={cl.banner}
										lazy
									/>
									<div className={cl.grid}>
										{/* Проверяем, есть ли баннер для секции */}
										{!!block.banners.banner_desktop.src && (
											<CustomImageBanner
												imgDeskWidth={556}
												imgDeskHeight={468}
												imgTabletWidth={620}
												imgTabletHeight={500}
												imgMobWidth={600}
												imgMobHeight={560}
												imgSrcDesk={BASE_URL + block.banners.banner_desktop.src}
												imgSrcTablet={BASE_URL + block.banners.banner_tablet.src}
												imgSrcMob={BASE_URL + block.banners.banner_mobile.src}
												link={block.banners.banner_desktop.link}
												className={cl.grid__item_banner}
												lazy
											/>
										)}

										{/* Товары */}
										<GridWithBannerSection items={products.filter(item => !!item.SECTION_NAME).slice(0, 8)} />
									</div>
									<Link prefetch={false} href={block.banners.banner_desktop.link}>
										<Button theme={'outlined'} className={cl.btn}>
											Смотреть все
										</Button>
									</Link>
								</section>
							)
						}
					}

					case 'banners_list':
						return (
							<section key={index} className={cl.section_db}>
								<div className={cl.doubleBanner_wrap}>
									<CustomImageBanner
										imgDeskWidth={556}
										imgDeskHeight={468}
										imgTabletWidth={620}
										imgTabletHeight={500}
										imgMobWidth={600}
										imgMobHeight={560}
										imgSrcDesk={BASE_URL + block.banners.banner_desktop[0].src}
										imgSrcTablet={BASE_URL + block.banners.banner_tablet[0].src}
										imgSrcMob={BASE_URL + block.banners.banner_mobile[0].src}
										link={block.banners.banner_desktop[0].link}
										className={cl.doubleBanner}
										lazy
									/>
									{/* Второй баннер */}
									<CustomImageBanner
										imgDeskWidth={556}
										imgDeskHeight={468}
										imgTabletWidth={620}
										imgTabletHeight={500}
										imgMobWidth={600}
										imgMobHeight={560}
										imgSrcDesk={BASE_URL + block.banners.banner_desktop[1].src}
										imgSrcTablet={BASE_URL + block.banners.banner_tablet[1].src}
										imgSrcMob={BASE_URL + block.banners.banner_mobile[1].src}
										link={block.banners.banner_desktop[1].link}
										className={cl.doubleBanner}
										lazy
									/>
								</div>
							</section>
						)

					case 'sections':
						return <ObserverList key={index} items={block.sections} />

					case 'why_iport':
						return <WhyPort key={index} />

					case 'prototype_text':
						return <ExtraList key={index} />

					case 'preorder_reasons':
						return <ListSection key={index} list={listData} />

					case 'limited_box':
						return (
							<Fragment key={index}>
								<h2 className={cl.sectionTitle}>{block.title}</h2>
								<BannerSliderSert />
							</Fragment>
						)

					case 'cert_limited_box':
						return (
							<Fragment key={index}>
								<h2 className={cl.sectionTitle}>{block.title}</h2>
								<BannerSlider />
							</Fragment>
						)

					case 'how_to_preorder':
						return (
							<section className={cl.steps} key={index}>
								<h2 className={cl.stepsTitle}>
									Как правильно <br />
									оформить предзаказ?
								</h2>
								<VerticalSteps items={stepsItems} />
							</section>
						)
					case 'terms':
						return <p className={cl.note} key={index} dangerouslySetInnerHTML={createMarkup(block.terms)} />

					case 'cert':
						return (
							<section className={cl.section} key={index}>
								<h2 className={cl.sectionTitle}>{block.title}</h2>
								<Sertificate />
							</section>
						)

					case 'preorder_gifts':
						return (
							<section key={index} id={block.code} className={cl.section}>
								<h2 className={cl.sectionTitle}>{block.title}</h2>
								<GiftsSection />
							</section>
						)

					case 'you_also_receive':
						return (
							<section key={index} id={block.code} className={cl.section}>
								<h2 className={cl.sectionTitle}>{block.title}</h2>
								<BenefitsSection />
							</section>
						)
					default:
						return null
				}
			})}
		</div>
	)
}
